import { useState } from 'react';
import { supabase } from '../../lib/supabase';

/**
 * Admin → Saleroom → "Test notifications" card.
 *
 * One click runs the REAL pipeline end to end: enqueues a 'test' row and
 * drains the queue exactly like the minutely cron does (POST
 * /api/admin/notifications/test) — Resend send, Telnyx send, consent +
 * suppression checks, email_log/sms_log writes. Errors come back precise
 * ("email_not_configured", "telnyx_send_failed (401)…"), so this doubles as
 * the misconfiguration detector after any env/dashboard change.
 *
 * Email tests work as soon as the code is deployed; SMS tests deliver only
 * once the Telnyx toll-free number passes verification (carriers block
 * unverified toll-free traffic outright).
 */

interface TestResult {
  ok?: boolean;
  claimed?: number;
  sent?: number;
  skipped?: number;
  failed?: number;
  details?: Array<{ id: string; event: string; status: string; note?: string }>;
  error?: string;
  detail?: string;
}

export function NotificationTester() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    if (busy) return;
    if (!email.trim() && !phone.trim()) {
      setError('Enter an email, a phone number (E.164, e.g. +15551234567), or both.');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Sign in first.');
      const res = await fetch('/api/admin/notifications/test', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        }),
      });
      const data: TestResult = await res.json();
      if (!res.ok) throw new Error(data?.detail || data?.error || `Failed (${res.status})`);
      setResult(data);
    } catch (err: any) {
      setError(err?.message ?? 'Test failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="notiftest">
      <div className="admin__panel-head notiftest__head">
        <h2>Test notifications</h2>
      </div>
      <p className="notiftest__lede">
        Sends a test through the real saleroom pipeline (queue → consent and
        suppression checks → Resend / Telnyx → logs). Email works once deployed;
        SMS delivers only after the Telnyx toll-free number is verified.
      </p>
      <div className="notiftest__row">
        <input
          className="notiftest__input"
          type="email"
          placeholder="Email to test (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          aria-label="Test email address"
        />
        <input
          className="notiftest__input"
          type="tel"
          placeholder="Phone to test, E.164 e.g. +15551234567 (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={busy}
          aria-label="Test phone number"
        />
        <button type="button" className="btn btn--primary btn--md" onClick={send} disabled={busy}>
          {busy ? 'Sending…' : 'Send test'}
        </button>
      </div>

      {error && <p className="notiftest__error" role="alert">{error}</p>}

      {result && (
        <div className="notiftest__result" role="status">
          <p>
            <strong>{result.sent ? 'Sent.' : 'Nothing sent.'}</strong>{' '}
            claimed {result.claimed ?? 0} · sent {result.sent ?? 0} · skipped{' '}
            {result.skipped ?? 0} · failed {result.failed ?? 0}
          </p>
          {(result.details ?? [])
            .filter((d) => d.note)
            .map((d) => (
              <p key={d.id} className="notiftest__note">
                {d.event}: {d.status} — {d.note}
              </p>
            ))}
        </div>
      )}
    </div>
  );
}
